import type { Point } from "./progress.js";

// PG-42: real-sample downsampling for charts. `WagmiLineChart.tsx` passes
// `curveLinear` on purpose ("a smoothed curve draws values that never
// existed"), so this keeps actual samples, never an average.
//
// One pixel column keeps at most 4 points: the first and last by x (the
// column's own time span) and the min/max by y (a spike inside it),
// deduplicated. A column with <=4 samples keeps every one, untouched.
// Columns are measured on the axis `wagmiChartModel.ts`'s `wagmiAxisRange`
// computes ONCE per chart (oldest sample of ANY series -> now), so two
// series' buckets line up at the same x.
//
// It returns kept INDICES, not points: the chart decimates an already
// annotated series, and `points` and `dots` must keep the SAME positions.

export type DecimateAxis = { x0: number; xEnd: number; columns: number };

/** The kept indices into `points`, ascending, at most ~4 per pixel column
 *  (first, last, min-y, max-y, deduplicated; a column with <=4 samples
 *  keeps every one). Passthrough (every index, in order) when
 *  `columns <= 0`, the `width === 0` case before a chart's first
 *  `onLayout`. Requires `points` ALREADY ascending by `x`: the same
 *  contract `annotateChartSeries` (`chartAnnotations.ts`) relies on for
 *  its own neighbour math, so a caller sitting between the two stages
 *  never needs to re-sort what was already sorted for it. */
export function decimateIndices(points: readonly Point[], axis: DecimateAxis): number[] {
  const { x0, xEnd, columns } = axis;
  const n = points.length;
  if (columns <= 0 || n === 0) return points.map((_, i) => i);

  const xSpan = xEnd - x0 || 1;

  // column -> indices into `points`, ascending within each bucket because
  // `points` itself is required ascending (see the doc comment above).
  const buckets = new Map<number, number[]>();
  points.forEach((p, i) => {
    const raw = Math.floor(((p.x - x0) / xSpan) * columns);
    const col = Math.min(columns - 1, Math.max(0, raw));
    const idxs = buckets.get(col);
    if (idxs) idxs.push(i);
    else buckets.set(col, [i]);
  });

  const kept = new Set<number>();
  for (const idxs of buckets.values()) {
    if (idxs.length <= 4) {
      idxs.forEach((i) => kept.add(i));
      continue;
    }
    // Safe: this branch only runs when `idxs.length > 4`, so index 0 and
    // every `i` drawn from `idxs` below are real entries, not holes.
    let minI = idxs[0]!;
    let maxI = idxs[0]!;
    for (const i of idxs) {
      if (points[i]!.y < points[minI]!.y) minI = i;
      if (points[i]!.y > points[maxI]!.y) maxI = i;
    }
    kept.add(idxs[0]!);
    kept.add(idxs[idxs.length - 1]!);
    kept.add(minI);
    kept.add(maxI);
  }

  return [...kept].sort((a, b) => a - b);
}
